import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ShieldCheck } from "lucide-react";
import {
  Breadcrumbs, Container, H2, H3, Ordinal, PageHero, Section,
} from "../components/Primitives";
import { getConfig } from "../lib/api";
import { useSEO, breadcrumbSchema } from "../lib/seo";
import { COMPANY } from "../content/nav";
import { Reveal, Stagger, StaggerItem } from "../lib/motion";

export default function Promises() {
  const { data } = useQuery({ queryKey: ["config"], queryFn: getConfig });
  const promises = data?.promises || [];
  const stack = data?.guarantee_stack || [];

  useSEO({
    title: "The Twelve Promises & The Guarantee Stack",
    description:
      "Twelve commitments written so you can catch us if we fail, six of them backed by a specific self-enforcing remedy. Verification, independent testing, loose-first inspection, 72-hour dispatch, no meter and zero commission.",
    canonical: "/promises",
    jsonLd: [
      breadcrumbSchema([{ label: "Home", to: "/" }, { label: "The Twelve Promises" }]),
      stack.length
        ? {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: stack.map((g) => ({
              "@type": "Question",
              name: `What is ${g.name}?`,
              acceptedAnswer: { "@type": "Answer", text: `${g.text} (${g.type})` },
            })),
          }
        : null,
    ],
  });

  return (
    <>
      <Container className="pt-6">
        <Breadcrumbs trail={[{ label: "Home", to: "/" }, { label: "The Twelve Promises" }]} />
      </Container>

      <PageHero
        eyebrow="Also: The Guarantee Stack"
        lines={["The Twelve", "Promises"]}
        lede="Every business in this category says it is trustworthy. Very few of them will tell you how to check. These are the twelve things we commit to, written so that you can catch us if we fail. Promises 1 through 6 are backed by a specific, self-enforcing remedy — that's what makes them guarantees rather than intentions, and it's why we list them together as The Guarantee Stack in every place we ask you to make a decision."
        meta={
          <p className="ui measure mt-6 border-l-2 border-l-gold pl-5 text-base leading-[1.7] text-gold">
            Six of them are guarantees, in the strict sense: if we fail, you get your money back, and
            here's exactly how.
          </p>
        }
      />

      {/* Palette cleanser — the stack reads as a certificate, not a sales page */}
      <Section className="force-dark border-b border-rule">
        <Container>
          <Reveal>
            <p className="eyebrow !text-gold-bright">Six remedies, not six adjectives</p>
            <H2 className="mt-5 max-w-3xl">The Guarantee Stack</H2>
          </Reveal>
          <Stagger className="mt-14 grid gap-px bg-rule sm:grid-cols-2 lg:grid-cols-3" stagger={0.08} data-testid="guarantee-stack">
            {stack.map((g) => (
              <StaggerItem key={g.name} className="bg-[#141310] p-8">
                <ShieldCheck className="h-4 w-4 text-verify" aria-hidden="true" />
                <H3 className="mt-4">{g.name}</H3>
                <p className="ui mt-1.5 text-[11px] uppercase tracking-[0.14em] text-gold-bright">{g.type}</p>
                <p className="ui mt-4 text-sm leading-[1.7] text-wash/80">{g.text}</p>
              </StaggerItem>
            ))}
          </Stagger>
        </Container>
      </Section>

      <Section>
        <Container>
          <Reveal>
            <H2 className="mb-4">All twelve, with the mechanism</H2>
            <p className="ui measure text-sm leading-[1.7] text-dim">
              Each promise carries the exact thing you can do if we fail it. A promise without a mechanism
              is marketing.
            </p>
          </Reveal>

          <Stagger className="mt-14 divide-y divide-rule" stagger={0.05} data-testid="promises-list">
            {promises.map((p) => (
              <StaggerItem
                key={p.n}
                className="grid gap-6 py-10 lg:grid-cols-[90px_1fr] lg:gap-10"
                data-testid={`promise-${p.n}`}
              >
                <Ordinal n={p.n} />
                <div>
                  <H3 className="h2-sub">{p.title}</H3>
                  {p.guarantee && (
                    <p className="ui mt-2 text-xs uppercase tracking-[0.14em] text-verify">
                      {p.guarantee} · {p.type}
                    </p>
                  )}
                  <p className="measure ui mt-4 text-sm leading-[1.7] text-dim">{p.body}</p>
                  <p className="measure ui mt-5 border-l-2 border-l-gold pl-5 text-sm leading-[1.7] text-ink">
                    <strong>How to hold us to it:</strong> {p.hold}
                  </p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>

          <Reveal delay={0.08}>
            <div className="card-s elev mt-16 p-8 lg:p-12">
              <H2>If we break one of these, tell us</H2>
              <p className="measure mt-5 text-[15px] leading-[1.75] text-dim">
                Email {COMPANY.email} — every message reaches a person, not a queue, and is answered
                within 24 hours. If we got it wrong, we'll say so publicly and fix it. That is a
                less comfortable promise than the other eleven, which is rather the point.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/about/scoreboard" className="btn btn-primary">See the numbers we publish</Link>
                <Link to="/legal/grievance" className="btn btn-secondary">Grievance redressal</Link>
              </div>
            </div>
          </Reveal>
        </Container>
      </Section>
    </>
  );
}
